import type { WaChat, WaGatewayConfig, WaInstance, WaInstanceCreated, WaQr } from '@erp/shared';
import {
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  MessageCircle,
  Plus,
  Power,
  QrCode,
  RefreshCw,
  Send,
  Server,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '../../components/shadcn/index.js';
import { Button, FormField, confirmDialog } from '../../components/ui/index.js';
import { formatTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';

/** Same rule the backend enforces — Evolution instance names are path segments
 *  (the lookahead rejects dot-only names like "." / ".."). */
const NAME_RE = /^(?!\.+$)[a-zA-Z0-9._-]{1,40}$/;

function stateVariant(state: string): 'success' | 'warning' | 'secondary' {
  if (state === 'open') return 'success';
  if (state === 'connecting') return 'warning';
  return 'secondary';
}

/** Evolution sometimes returns the QR with the data-URI prefix already on it. */
function qrSrc(qrBase64: string): string {
  return qrBase64.startsWith('data:') ? qrBase64 : `data:image/png;base64,${qrBase64}`;
}

function errCode(e: unknown): string | undefined {
  return (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
}

/** Unix seconds → local HH:MM (empty when unknown). */
function fmtTime(ts: number, locale: string): string {
  if (!ts) return '';
  try {
    return formatTime(new Date(ts * 1000), { locale });
  } catch {
    return '';
  }
}

/**
 * Platform-admin console for the self-hosted Evolution API WhatsApp gateway.
 * Manages instances (one WhatsApp login each) end-to-end: create → scan QR →
 * connected, restart, logout+delete, per-instance token reveal, and a test
 * message. Everything is proxied through the api — the gateway master key
 * never reaches the browser.
 */
export function PlatformWhatsAppPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const [config, setConfig] = useState<WaGatewayConfig | null>(null);
  const [rows, setRows] = useState<WaInstance[] | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  // QR dialog (shared by create + "Show QR" on an existing instance)
  const [qrFor, setQrFor] = useState<string | null>(null);
  const [qr, setQr] = useState<WaQr | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [connected, setConnected] = useState(false);

  // Test console (send + recent messages)
  const [testFor, setTestFor] = useState<string | null>(null);
  const [testNumber, setTestNumber] = useState('');
  const [testText, setTestText] = useState('');
  const [sending, setSending] = useState(false);
  const [chats, setChats] = useState<WaChat[] | null>(null);
  const [chatsLoading, setChatsLoading] = useState(false);

  const loadConfig = useCallback(async (): Promise<void> => {
    try {
      const r = await platformApi().get<WaGatewayConfig>('/whatsapp/config');
      setConfig(r.data);
    } catch {
      toast.error(t('whatsapp.loadError', { defaultValue: 'Could not reach the gateway config.' }));
    }
  }, [t]);

  const loadInstances = useCallback(async (): Promise<void> => {
    try {
      const r = await platformApi().get<{ items: WaInstance[] }>('/whatsapp/instances');
      setRows(r.data.items);
    } catch {
      setRows([]);
      toast.error(t('whatsapp.listError', { defaultValue: 'Could not load the instances.' }));
    }
  }, [t]);

  useEffect(() => {
    void loadConfig();
    void loadInstances();
  }, [loadConfig, loadInstances]);

  // Poll the connection state every 3s while the QR dialog is open, until the
  // scan lands — same self-cancelling interval pattern as FibPayBox.
  useEffect(() => {
    if (!qrFor || connected) return undefined;
    const id = window.setInterval(() => {
      void platformApi()
        .get<{ state: string }>(`/whatsapp/instances/${encodeURIComponent(qrFor)}/state`)
        .then((r) => {
          if (r.data.state === 'open') {
            setConnected(true);
            window.clearInterval(id);
            void loadInstances();
          }
        })
        .catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(id);
  }, [qrFor, connected, loadInstances]);

  const fetchQr = useCallback(async (instanceName: string): Promise<void> => {
    setQrLoading(true);
    try {
      const r = await platformApi().get<WaQr>(
        `/whatsapp/instances/${encodeURIComponent(instanceName)}/qr`,
      );
      setQr(r.data);
      if (r.data.state === 'open') setConnected(true);
    } catch {
      toast.error(t('whatsapp.qrError', { defaultValue: 'Could not fetch a QR code.' }));
    } finally {
      setQrLoading(false);
    }
  }, [t]);

  const openQr = (instanceName: string): void => {
    setQrFor(instanceName);
    setQr(null);
    setConnected(false);
    void fetchQr(instanceName);
  };

  const closeQr = (): void => {
    setQrFor(null);
    setQr(null);
    setConnected(false);
  };

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!NAME_RE.test(trimmed)) {
      toast.error(t('whatsapp.nameInvalid', {
        defaultValue: 'Name must be 1–40 characters: letters, digits, dots, dashes, underscores.',
      }));
      return;
    }
    setCreating(true);
    try {
      const r = await platformApi().post<WaInstanceCreated>('/whatsapp/instances', { name: trimmed });
      setCreateOpen(false);
      setName('');
      // Jump straight into the QR dialog with whatever the create returned.
      setQrFor(r.data.name);
      setQr({ state: r.data.state, qrBase64: r.data.qrBase64, pairingCode: r.data.pairingCode });
      setConnected(r.data.state === 'open');
      if (!r.data.qrBase64 && r.data.state !== 'open') void fetchQr(r.data.name);
      await loadInstances();
    } catch (err) {
      const code = errCode(err);
      toast.error(code === 'instance_exists' || code === 'conflict'
        ? t('whatsapp.nameTaken', { defaultValue: 'An instance with that name already exists.' })
        : t('whatsapp.createError', { defaultValue: 'Could not create the instance.' }));
    } finally {
      setCreating(false);
    }
  };

  const onRestart = async (i: WaInstance): Promise<void> => {
    setBusy(i.name);
    try {
      await platformApi().post(`/whatsapp/instances/${encodeURIComponent(i.name)}/restart`, {});
      toast.success(t('whatsapp.restarted', { defaultValue: '{{name}} is restarting.', name: i.name }));
      await loadInstances();
    } catch {
      toast.error(t('whatsapp.restartError', { defaultValue: 'Could not restart the instance.' }));
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (i: WaInstance): Promise<void> => {
    const ok = await confirmDialog({
      message: t('whatsapp.deleteConfirm', {
        defaultValue: 'Log out and delete {{name}}? Anything using its token stops sending immediately.',
        name: i.name,
      }),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(i.name);
    try {
      await platformApi().delete(`/whatsapp/instances/${encodeURIComponent(i.name)}`);
      toast.success(t('whatsapp.deleted', { defaultValue: '{{name}} deleted.', name: i.name }));
      // Optimistic removal — Evolution's delete is eventually consistent, so an
      // immediate refetch resurrects the row as a 'close' ghost for a few
      // seconds. The next natural load converges with the gateway.
      setRows((r) => (r ? r.filter((x) => x.name !== i.name) : r));
    } catch {
      toast.error(t('whatsapp.deleteError', { defaultValue: 'Could not delete the instance.' }));
    } finally {
      setBusy(null);
    }
  };

  const onSendTest = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!testFor) return;
    const digits = testNumber.replace(/\D/g, '');
    if (digits.length < 6 || digits.length > 20) {
      toast.error(t('whatsapp.numberInvalid', {
        defaultValue: 'Enter a full international number, e.g. 9647501234567.',
      }));
      return;
    }
    if (!testText.trim()) {
      toast.error(t('whatsapp.textRequired', { defaultValue: 'Write a message to send.' }));
      return;
    }
    setSending(true);
    try {
      await platformApi().post(`/whatsapp/instances/${encodeURIComponent(testFor)}/test`, {
        number: testNumber.trim(),
        text: testText.trim(),
      });
      toast.success(t('whatsapp.testSent', { defaultValue: 'Test message sent.' }));
      // Keep the console open, clear only the message, and refresh the chats so
      // the just-sent message shows up.
      setTestText('');
      void loadChats(testFor);
    } catch {
      toast.error(t('whatsapp.testError', {
        defaultValue: 'Send failed — is the instance connected and the number on WhatsApp?',
      }));
    } finally {
      setSending(false);
    }
  };

  const loadChats = useCallback(async (instanceName: string): Promise<void> => {
    setChatsLoading(true);
    try {
      const r = await platformApi().get<{ items: WaChat[] }>(
        `/whatsapp/instances/${encodeURIComponent(instanceName)}/chats`,
      );
      setChats(r.data.items);
    } catch {
      setChats([]);
    } finally {
      setChatsLoading(false);
    }
  }, []);

  const openTest = (instanceName: string): void => {
    setTestFor(instanceName);
    setTestNumber('');
    setTestText('');
    setChats(null);
    void loadChats(instanceName);
  };

  const closeTest = (): void => {
    setTestFor(null);
    setChats(null);
  };

  const copyToken = (i: WaInstance): void => {
    if (!i.token) return;
    void navigator.clipboard.writeText(i.token);
    toast.success(t('whatsapp.tokenCopied', { defaultValue: 'Token copied.' }));
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-success/15 text-success">
            <MessageCircle className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('whatsapp.title', { defaultValue: 'WhatsApp gateway' })}</h1>
            <p className="text-sm text-fg-muted">
              {t('whatsapp.subtitle', { defaultValue: 'Manage the WhatsApp gateway instances — one WhatsApp login each, usable from any project via its token.' })}
            </p>
          </div>
        </div>
        {config?.configured && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="me-2 h-4 w-4" aria-hidden />
            {t('whatsapp.newInstance', { defaultValue: 'New instance' })}
          </Button>
        )}
      </header>

      {/* Gateway health */}
      <Card className="glass-panel p-4">
        {config === null ? (
          <Skeleton className="h-10 w-full" />
        ) : !config.configured ? (
          <div className="flex items-start gap-3">
            <Server className="mt-0.5 h-5 w-5 text-fg-muted" aria-hidden />
            <div>
              <h2 className="text-sm font-semibold">{t('whatsapp.notConfigured', { defaultValue: 'Gateway not configured' })}</h2>
              <p className="mt-1 text-sm text-fg-muted">
                {t('whatsapp.notConfiguredHint', { defaultValue: 'Set EVOLUTION_URL and EVOLUTION_API_KEY in the api environment, then restart the api. The master key stays server-side — it is never sent to the browser.' })}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-fg-muted" aria-hidden />
              <span className="font-mono">{config.serverUrl}</span>
            </div>
            <Badge variant={config.reachable ? 'success' : 'danger'}>
              {config.reachable
                ? t('whatsapp.reachable', { defaultValue: 'Reachable' })
                : t('whatsapp.unreachable', { defaultValue: 'Unreachable' })}
            </Badge>
            {config.version && (
              <span className="text-fg-muted">
                {t('whatsapp.version', { defaultValue: 'Gateway v{{v}}', v: config.version })}
              </span>
            )}
          </div>
        )}
      </Card>

      {/* Instances */}
      {config?.configured && (
        <Card className="glass-panel p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
            {t('whatsapp.instances', { defaultValue: 'Instances' })}
          </h2>
          {rows === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t('whatsapp.empty', { defaultValue: 'No instances yet — create one and scan its QR with the phone that owns the number.' })}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
              {rows.map((i) => (
                <li key={i.name} className="flex flex-col gap-3 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {i.profilePictureUrl && <AvatarImage src={i.profilePictureUrl} alt="" />}
                        <AvatarFallback className="bg-bg-subtle text-fg-muted">
                          <MessageCircle className="h-5 w-5" aria-hidden />
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-mono text-sm font-semibold">{i.name}</span>
                          <Badge variant={stateVariant(i.state)}>
                            {t(`whatsapp.state.${i.state}`, { defaultValue: i.state })}
                          </Badge>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-fg-muted">
                          {i.profileName ?? '—'}
                          {i.ownerPhone && <span className="ms-2 font-mono" dir="ltr">+{i.ownerPhone.replace(/^\+/, '')}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {i.state !== 'open' && (
                        <Button variant="secondary" size="sm" onClick={() => openQr(i.name)} disabled={busy === i.name}>
                          <QrCode className="me-2 h-4 w-4" aria-hidden />
                          {t('whatsapp.showQr', { defaultValue: 'Show QR' })}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openTest(i.name)}
                        disabled={busy === i.name || i.state !== 'open'}
                      >
                        <Send className="me-2 h-4 w-4" aria-hidden />
                        {t('whatsapp.testConsole', { defaultValue: 'Test / messages' })}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => void onRestart(i)} loading={busy === i.name}>
                        <Power className="me-2 h-4 w-4" aria-hidden />
                        {t('whatsapp.restart', { defaultValue: 'Restart' })}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void onDelete(i)} disabled={busy === i.name} aria-label={tc('actions.delete', { defaultValue: 'Delete' })}>
                        <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                      </Button>
                    </div>
                  </div>

                  {i.token && (
                    <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-subtle/40 px-3 py-2">
                      <span className="text-xs uppercase tracking-wide text-fg-muted">
                        {t('whatsapp.token', { defaultValue: 'Token' })}
                      </span>
                      <code className="min-w-0 flex-1 truncate font-mono text-xs" dir="ltr">
                        {revealed[i.name] ? i.token : '••••••••••••••••••••••••'}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevealed((r) => ({ ...r, [i.name]: !r[i.name] }))}
                        aria-label={revealed[i.name]
                          ? t('whatsapp.hideToken', { defaultValue: 'Hide token' })
                          : t('whatsapp.revealToken', { defaultValue: 'Reveal token' })}
                      >
                        {revealed[i.name] ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyToken(i)} aria-label={t('whatsapp.copyToken', { defaultValue: 'Copy token' })}>
                        <Copy className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('whatsapp.createTitle', { defaultValue: 'New WhatsApp instance' })}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void onCreate(e)} className="flex flex-col gap-4" noValidate>
            <FormField
              id="wa-name"
              label={t('whatsapp.name', { defaultValue: 'Instance name' })}
              helperText={t('whatsapp.nameHint', { defaultValue: 'Letters, digits, dots, dashes, underscores — e.g. erp-notifications.' })}
              required
            >
              <Input
                id="wa-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="erp-notifications"
                autoCapitalize="none"
                className="font-mono"
                maxLength={40}
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                {tc('actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" loading={creating}>
                {t('whatsapp.create', { defaultValue: 'Create' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrFor !== null} onOpenChange={(o) => { if (!o) closeQr(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('whatsapp.qrTitle', { defaultValue: 'Connect {{name}}', name: qrFor ?? '' })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {connected ? (
              <>
                <CheckCircle2 className="h-14 w-14 text-success" aria-hidden />
                <p className="text-sm font-semibold text-success">
                  {t('whatsapp.connected', { defaultValue: 'Connected!' })}
                </p>
              </>
            ) : qrLoading && !qr?.qrBase64 ? (
              <Skeleton className="h-56 w-56" />
            ) : qr?.qrBase64 ? (
              <>
                <img
                  src={qrSrc(qr.qrBase64)}
                  alt={t('whatsapp.qrAlt', { defaultValue: 'WhatsApp pairing QR code' })}
                  className="h-56 w-56 rounded-md bg-white p-2"
                />
                {qr.pairingCode && (
                  <p className="text-sm text-fg-muted">
                    {t('whatsapp.pairingCode', { defaultValue: 'Pairing code:' })}{' '}
                    <code className="font-mono font-semibold text-fg" dir="ltr">{qr.pairingCode}</code>
                  </p>
                )}
                <p className="text-center text-xs text-fg-muted">
                  {t('whatsapp.qrHint', { defaultValue: 'On the phone: WhatsApp → Settings → Linked devices → Link a device, then scan. This page detects the connection automatically.' })}
                </p>
              </>
            ) : (
              <p className="py-6 text-sm text-fg-muted">
                {t('whatsapp.qrPending', { defaultValue: 'No QR yet — refresh in a moment.' })}
              </p>
            )}
          </div>
          <DialogFooter>
            {!connected && (
              <Button
                variant="secondary"
                onClick={() => { if (qrFor) void fetchQr(qrFor); }}
                loading={qrLoading}
              >
                <RefreshCw className="me-2 h-4 w-4" aria-hidden />
                {t('whatsapp.refreshQr', { defaultValue: 'Refresh QR' })}
              </Button>
            )}
            <Button variant={connected ? 'primary' : 'ghost'} onClick={closeQr}>
              {connected
                ? t('whatsapp.done', { defaultValue: 'Done' })
                : tc('actions.close', { defaultValue: 'Close' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test console — send a message + see the recent history */}
      <Dialog open={testFor !== null} onOpenChange={(o) => { if (!o && !sending) closeTest(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('whatsapp.consoleTitle', { defaultValue: 'Test console — {{name}}', name: testFor ?? '' })}
            </DialogTitle>
          </DialogHeader>

          {/* Recent chats (last 5), each with a few messages */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
              {t('whatsapp.recentChats', { defaultValue: 'Recent chats' })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { if (testFor) void loadChats(testFor); }}
              loading={chatsLoading}
              aria-label={tc('actions.refresh', { defaultValue: 'Refresh' })}
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
            </Button>
          </div>
          <div className="flex max-h-72 min-h-[7rem] flex-col gap-2 overflow-y-auto rounded-lg border border-border bg-bg-subtle/30 p-2">
            {chats === null && chatsLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : !chats || chats.length === 0 ? (
              <p className="m-auto max-w-[17rem] text-center text-sm text-fg-muted">
                {t('whatsapp.noChats', { defaultValue: 'No chats yet. Send a message below to start one — it will show up here.' })}
              </p>
            ) : (
              chats.map((ch) => (
                <div key={ch.remoteJid} className="rounded-lg border border-border bg-bg p-2">
                  {/* Tap the header to reply — prefills the composer number */}
                  <button
                    type="button"
                    onClick={() => { if (ch.number) setTestNumber(ch.number); }}
                    className="mb-1.5 flex w-full items-center justify-between gap-2 text-start"
                    title={t('whatsapp.replyHint', { defaultValue: 'Use this number in the composer' })}
                  >
                    <span className="truncate text-sm font-semibold">
                      {ch.name ?? (ch.number ? `+${ch.number}` : ch.remoteJid)}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-fg-muted" dir="ltr">{fmtTime(ch.lastTimestamp, i18n.language)}</span>
                  </button>
                  <div className="flex flex-col gap-1">
                    {ch.messages.map((m) => (
                      <div key={m.id || String(m.timestamp)} className={`flex ${m.fromMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-xl px-2.5 py-1 text-xs ${m.fromMe ? 'bg-brand text-brand-contrast' : 'border border-border bg-bg-subtle'}`}>
                          <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Compose */}
          <form onSubmit={(e) => void onSendTest(e)} className="mt-1 flex flex-col gap-3" noValidate>
            <FormField
              id="wa-test-number"
              label={t('whatsapp.testNumber', { defaultValue: 'Number (international, digits only)' })}
              required
            >
              <Input
                id="wa-test-number"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="9647501234567"
                inputMode="tel"
                dir="ltr"
                className="font-mono"
              />
            </FormField>
            <div className="flex items-center gap-2">
              <Input
                aria-label={t('whatsapp.testText', { defaultValue: 'Message' })}
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder={t('whatsapp.testTextPh', { defaultValue: 'Type a message to send…' })}
                className="flex-1"
              />
              <Button type="submit" loading={sending} disabled={!testNumber.trim() || !testText.trim()} aria-label={t('whatsapp.send', { defaultValue: 'Send' })}>
                <Send className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </form>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeTest} disabled={sending}>
              {tc('actions.close', { defaultValue: 'Close' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
